import { createSlice } from '@reduxjs/toolkit';
import { UsersManagementState } from '../../types/userManagement';
import {
  createCompanyUserThunk,
  fetchCompanyUsersThunk,
  updateCompanyUserStatusThunk,
  assignUserLoginCodeThunk,
  regenerateUserLoginCodeThunk,
  fetchUserRoleMetricsThunk,
} from './userManagementThunks';

const initialState: UsersManagementState = {
  users: [],
  metrics: [],
  isLoading: false,
  isMetricsLoading: false,
  isCreating: false,
  statusUpdatingUserId: null,
  error: null,
};

const userManagementSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearUsersError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyUsersThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyUsersThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload;
      })
      .addCase(fetchCompanyUsersThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch employee accounts';
      })
      .addCase(createCompanyUserThunk.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createCompanyUserThunk.fulfilled, (state, action) => {
        state.isCreating = false;
        state.users = [...state.users, action.payload].sort((left, right) => {
          const leftOutlet = left.outlet_name || '';
          const rightOutlet = right.outlet_name || '';
          if (leftOutlet !== rightOutlet) {
            return leftOutlet.localeCompare(rightOutlet);
          }

          return `${left.first_name} ${left.last_name}`.localeCompare(
            `${right.first_name} ${right.last_name}`,
          );
        });
      })
      .addCase(createCompanyUserThunk.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload || 'Failed to create employee account';
      })
      .addCase(updateCompanyUserStatusThunk.pending, (state, action) => {
        state.statusUpdatingUserId = action.meta.arg.userId;
        state.error = null;
      })
      .addCase(updateCompanyUserStatusThunk.fulfilled, (state, action) => {
        state.statusUpdatingUserId = null;
        state.users = state.users.map((user) =>
          user.id === action.payload.id ? action.payload : user,
        );
      })
      .addCase(updateCompanyUserStatusThunk.rejected, (state, action) => {
        state.statusUpdatingUserId = null;
        state.error = action.payload || 'Failed to update employee status';
      })
      .addCase(assignUserLoginCodeThunk.pending, (state, action) => {
        state.statusUpdatingUserId = action.meta.arg.userId;
        state.error = null;
      })
      .addCase(assignUserLoginCodeThunk.fulfilled, (state, action) => {
        state.statusUpdatingUserId = null;
        state.users = state.users.map((user) =>
          user.id === action.payload.id ? action.payload : user,
        );
      })
      .addCase(assignUserLoginCodeThunk.rejected, (state, action) => {
        state.statusUpdatingUserId = null;
        state.error = action.payload || 'Failed to update employee passcode';
      })
      .addCase(regenerateUserLoginCodeThunk.pending, (state, action) => {
        state.statusUpdatingUserId = action.meta.arg;
        state.error = null;
      })
      .addCase(regenerateUserLoginCodeThunk.fulfilled, (state, action) => {
        state.statusUpdatingUserId = null;
        state.users = state.users.map((user) =>
          user.id === action.payload.id ? action.payload : user,
        );
      })
      .addCase(regenerateUserLoginCodeThunk.rejected, (state, action) => {
        state.statusUpdatingUserId = null;
        state.error =
          action.payload || 'Failed to regenerate employee passcode';
      })
      .addCase(fetchUserRoleMetricsThunk.pending, (state) => {
        state.isMetricsLoading = true;
        state.error = null;
      })
      .addCase(fetchUserRoleMetricsThunk.fulfilled, (state, action) => {
        state.isMetricsLoading = false;
        state.metrics = action.payload;
      })
      .addCase(fetchUserRoleMetricsThunk.rejected, (state, action) => {
        state.isMetricsLoading = false;
        state.error = action.payload || 'Failed to fetch user metrics';
      });
  },
});

export const { clearUsersError } = userManagementSlice.actions;
export default userManagementSlice.reducer;
